
import { useQueryClient } from "@tanstack/react-query";
import {
  Col,
  Form,
  Input,
  Modal,
  notification,
  Row,
  Select,
  Switch,
} from "antd";
import TextArea from "antd/es/input/TextArea";
import { useEffect } from "react";
import {
  useInventoryServiceGetServiceCategory,
  useInventoryServiceUpdateService,
  useInventoryServiceGetServicesKey,
} from "api/queries";
import AccountSelect from "components/common/AccountSelect";
import queryString from "query-string";
import { ServiceItem } from "api/requests";

export const UpdateServiceModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  service: ServiceItem;
}> = ({ isOpen, onClose, service }) => {
  const [form] = Form.useForm();
  const queryClient = useQueryClient();

  const { data: serviceCategoryData } = useInventoryServiceGetServiceCategory({
    filters: "",
    pagination: queryString.stringify({ limit: 100, offset: 0 }),
    sort: "",
  });

  const { mutate, isPending } = useInventoryServiceUpdateService({
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [useInventoryServiceGetServicesKey],
        refetchType: "all",
      });
      form.resetFields();
      onClose();
    },
    onError: (error: any) => {
      notification.error({
        message: "Failed to update service",
        description:
          error?.body?.message ||
          error?.response?.data?.message ||
          error?.message ||
          "An unexpected error occurred.",
      });
    },
  });

  useEffect(() => {
    if (service) {
      form.setFieldsValue({
        serviceName: service.serviceName,
        unitPrice: service.unitPrice,
        description: service.description,
        isActive: service.isActive,
        //@ts-ignore
        serviceCategory: service?.serviceCategory?.idServiceCategory,
        account: service?.defaultAccount,
      });
    }
  }, [service, form]);

  const handleSubmit = async (values: any) => {
    await mutate({
      idService: service.idService,
      requestBody: {
        serviceName: values.serviceName,
        description: values.description,
        serviceCategory: values.serviceCategory,
        isActive: values.isActive,
        defaultAccount: values.account,
        unitPrice: values.unitPrice,
      },
    });
  };

  return (
    <Modal
      title="Update Service"
      open={isOpen}
      onCancel={onClose}
      okText="Update"
      cancelText="Cancel"
      onOk={() => form.submit()}
      okButtonProps={{ form: "updateServiceForm", loading: isPending }}
      cancelButtonProps={{ disabled: isPending }}
    >
      <Form
        form={form}
        onFinish={handleSubmit}
        layout="vertical"
        id="updateServiceForm"
      >
        <Row gutter={16}>
          <Col xs={12}>
            <Form.Item
              label="Service Name"
              name="serviceName"
              rules={[{ required: true }]}
            >
              <Input />
            </Form.Item>
          </Col>

          <Col xs={12}>
            <Form.Item
              label="Unit Price"
              name="unitPrice"
              rules={[{ required: true }]}
            >
              <Input type="number" />
            </Form.Item>
          </Col>

          <Col xs={12}>
            <Form.Item
              label="Service Category"
              name="serviceCategory"
              rules={[{ required: true }]}
            >
              <Select
                options={
                  serviceCategoryData?.items
                    ? serviceCategoryData.items.map((d) => ({
                        label: d.serviceCategoryName,
                        value: d.idServiceCategory,
                      }))
                    : []
                }
              />
            </Form.Item>
          </Col>

          <Col xs={12}>
            <Form.Item
              label="Account"
              name="account"
              rules={[{ required: true }]}
            >
              <AccountSelect />
            </Form.Item>
          </Col>

          <Col xs={24}>
            <Form.Item
              label="Service Description"
              name="description"
              rules={[{ required: true }]}
            >
              <TextArea />
            </Form.Item>
          </Col>

          <Col xs={12}>
            <Form.Item
              label="Is Active"
              name="isActive"
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default UpdateServiceModal;